import React, { useState, useRef, useEffect } from 'react';
import {
  Checkbox,
  ListItemIcon,
  ListItemText,
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  MenuItem,
  Paper,
  ClickAwayListener,
  Chip
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';

interface Option {
  label: string;
  value: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: Option[];
  selected: string[];
  onApply: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onApply,
  placeholder,
  disabled = false,
  loading = false
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // When selection changes from outside or dropdown closes, update the search field
  useEffect(() => {
    if (!open) {
      setSearch(
        selected.length
          ? options
              .filter(opt => selected.includes(opt.value))
              .map(opt => opt.label)
              .join(', ')
          : ''
      );
    }
  }, [selected, open, options]);

  const handleToggle = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onApply(newSelected);
  };

  const filteredOptions = options.filter(opt =>
    opt.label?.toLowerCase().includes(search.toLowerCase())
  );

  const handleClickAway = () => {
    setOpen(false);
    setSearch(
      selected.length
        ? options
            .filter(opt => selected.includes(opt.value))
            .map(opt => opt.label)
            .join(', ')
        : ''
    );
  };

  return (
    <>
      <style>{`.MuiListItemIcon-root { min-width: 0 !important; }`}</style>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
          <TextField
            inputRef={inputRef}
            variant="outlined"
            placeholder={selected.length === 0 ? placeholder || label : ''}
            onChange={e => {
              setSearch(e.target.value);
              if (!open) setOpen(true);
            }}
            fullWidth
            disabled={disabled}
            onFocus={() => {
              if (!disabled) {
                setSearch('');
                setOpen(true);
              }
            }}
            InputProps={{
              startAdornment:
                selected.length > 0 && !open ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: 4, marginLeft: 4 }}
                  >
                    {selected.slice(0, 1).map(val => {
                      const opt = options.find(o => o.value === val);
                      return (
                        <Chip
                          key={val}
                          label={opt ? opt.label : val}
                          size="small"
                          style={{
                            marginRight: 2,
                            background: '#F3F2F7',
                            fontWeight: 500
                          }}
                          onDelete={e => {
                            if (disabled) return;
                            e.stopPropagation();
                            const newSelected = selected.filter(s => s !== val);
                            onApply(newSelected);
                          }}
                          deleteIcon={
                            disabled ? (
                              <ClearIcon
                                style={{
                                  fontSize: 18,
                                  opacity: 0.5,
                                  cursor: 'not-allowed'
                                }}
                              />
                            ) : (
                              <ClearIcon style={{ fontSize: 18 }} />
                            )
                          }
                        />
                      );
                    })}
                    {selected.length > 1 && (
                      <Chip
                        label={`+${selected.length - 1}`}
                        size="small"
                        style={{ background: '#F3F2F7', fontWeight: 500 }}
                      />
                    )}
                  </Box>
                ) : (
                  undefined
                ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() => !disabled && setOpen(prev => !prev)}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                </InputAdornment>
              ),
              style: {
                borderRadius: 4,
                background: '#fff',
                fontSize: 15
              }
            }}
          />

          {open && (
            <Paper
              elevation={4}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 320,
                minHeight: 120,
                width: '100%',
                border: '1px solid #e9e4e4'
              }}
            >
              <Box
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  maxHeight: 220,
                  padding: '0 0 0 0'
                }}
              >
                {loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{ height: 80 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : filteredOptions.length === 0 ? (
                  <Typography
                    color="textSecondary"
                    style={{ padding: 16, textAlign: 'center' }}
                  >
                    No options
                  </Typography>
                ) : (
                  filteredOptions.map(opt => (
                    <MenuItem
                      key={opt.value}
                      onClick={() => handleToggle(opt.value)}
                      selected={selected.includes(opt.value)}
                      style={{ borderRadius: 6, marginBottom: 2 }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selected.includes(opt.value)}
                          tabIndex={-1}
                          disableRipple
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))
                )}
              </Box>
            </Paper>
          )}
        </div>
      </ClickAwayListener>
    </>
  );
};

export default MultiSelectDropdown;
